/**
 * User-Role Assignment Page
 *
 * Admin interface for assigning and revoking roles from users.
 *
 * Features:
 * - List all users in the tenant
 * - View user's current roles
 * - Assign roles to users (with privilege escalation prevention)
 * - Revoke roles from users
 * - View user's effective permissions
 *
 * SOC 2 Controls: CC6.2, CC6.3, CC6.7 - User access management with RBAC
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';

// const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

export default function UserRoleAssignment({ user, onLogout, hideNavigation = false }) {
  const { showSuccess, showError, confirm } = useAlert();
  const [users, setUsers] = useState([]);
  const [userRolesMap, setUserRolesMap] = useState({}); // Map of userId -> array of RBAC roles
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const { token } = useAuth();
  const { hasPermission } = usePermissions();

  const canViewUsers = hasPermission('users', 'read');
  const canAssignRoles = hasPermission('roles', 'assign');

  useEffect(() => {
    if (token) {
      loadUsersWithRoles();
      loadRoles();
    }
  }, [token]);

  const loadUsersWithRoles = async () => {
    try {
      setLoading(true);

      // Load users
      const usersResponse = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!usersResponse.ok) throw new Error('Failed to load users');

      const usersData = await usersResponse.json();
      setUsers(usersData);

      // Load RBAC roles for each user
      const rolesMap = {};
      await Promise.all(
        usersData.map(async (user) => {
          try {
            const response = await fetch(`${API_BASE_URL}/rbac/users/${user.id}/roles`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.ok) {
              const userRoles = await response.json();
              rolesMap[user.id] = userRoles;
            } else {
              rolesMap[user.id] = [];
            }
          } catch (err) {
            console.error(`Failed to load roles for user ${user.id}:`, err);
            rolesMap[user.id] = [];
          }
        })
      );

      setUserRolesMap(rolesMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rbac/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleViewUserRoles = async (user) => {
    setSelectedUser(user);
    setShowPermissionsModal(true);
  };

  const handleAssignRole = (user) => {
    setSelectedUser(user);
    setShowAssignModal(true);
  };

  if (!canViewUsers) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          You do not have permission to view users.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  const content = (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Role Assignment</h1>
        <p className="text-gray-600 mt-1">Manage role assignments for users in your organization</p>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RBAC Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.email}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {userRolesMap[user.id] && userRolesMap[user.id].roles && userRolesMap[user.id].roles.length > 0 ? (
                      userRolesMap[user.id].roles.map((role) => (
                        <span key={role.id} className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {role.name}
                        </span>
                      ))
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                        No roles assigned
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.is_active ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewUserRoles(user)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View Roles & Permissions
                    </button>
                    {canAssignRoles && user.is_active && (
                      <button
                        onClick={() => handleAssignRole(user)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Manage Roles
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Role Modal */}
      {showAssignModal && selectedUser && (
        <AssignRoleModal
          user={selectedUser}
          roles={roles}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            loadUsersWithRoles();
          }}
          token={token}
          showSuccess={showSuccess}
          showError={showError}
          confirm={confirm}
        />
      )}

      {/* View User Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <UserPermissionsModal
          user={selectedUser}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedUser(null);
          }}
          token={token}
        />
      )}
    </>
  );

  // If embedded in another page (hideNavigation=true), return just the content
  if (hideNavigation) {
    return content;
  }

  // Otherwise, render with full page layout including navigation
  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </div>
    </div>
  );
}

/**
 * Assign Role Modal Component
 */
function AssignRoleModal({ user, roles, onClose, onSuccess, token, showSuccess, showError, confirm }) {
  const [userRoles, setUserRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUserRoles();
  }, [user.id]);

  const loadUserRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rbac/users/${user.id}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Failed to load user roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/rbac/users/${user.id}/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role_id: selectedRoleId
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to assign role');
      }

      await loadUserRoles();
      setSelectedRoleId('');
      showSuccess('Role assigned successfully');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeRole = async (roleId) => {
    const confirmed = await confirm('Are you sure you want to revoke this role from the user?');
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/rbac/users/${user.id}/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke role');
      }

      await loadUserRoles();
      showSuccess('Role revoked successfully');
      onSuccess();
    } catch (err) {
      showError(`Error: ${err.message}`);
    }
  };

  const availableRoles = roles.filter(
    role => !userRoles.some(ur => ur.id === role.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Manage Roles - {user.name}</h2>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading user roles...</div>
        ) : (
          <div>
            {/* Current Roles */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Current Roles</h3>
              {userRoles.length > 0 ? (
                <div className="space-y-2">
                  {userRoles.map((role) => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{role.name}</div>
                        <div className="text-sm text-gray-600">{role.description}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Level {role.role_level}
                          {role.is_system_role && ' • System Role'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeRole(role.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No roles assigned</p>
              )}
            </div>

            {/* Assign New Role */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Assign New Role</h3>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              <form onSubmit={handleAssignRole}>
                <div className="mb-4">
                  <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a role...</option>
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name} (Level {role.role_level})
                        {role.is_system_role && ' - System Role'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={submitting || !selectedRoleId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {submitting ? 'Assigning...' : 'Assign Role'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * User Permissions Modal Component
 */
function UserPermissionsModal({ user, onClose, token }) {
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserPermissions();
  }, [user.id]);

  const loadUserPermissions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rbac/users/${user.id}/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data);
      }
    } catch (err) {
      console.error('Failed to load user permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by resource
  const groupedPermissions = userPermissions?.permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">{user.name} - Roles & Permissions</h2>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : userPermissions ? (
          <div>
            {/* Roles */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Assigned Roles</h3>
              <div className="flex flex-wrap gap-2">
                {userPermissions.roles.map((role) => (
                  <span
                    key={role.id}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {role.name} (Level {role.role_level})
                  </span>
                ))}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-lg font-semibold mb-3">
                Effective Permissions ({userPermissions.permissions.length})
              </h3>
              <div className="space-y-4">
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <div key={resource} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 capitalize">{resource}</h4>
                    <div className="flex flex-wrap gap-2">
                      {perms.map((perm) => (
                        <span
                          key={perm.id}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                        >
                          {perm.action}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No permissions found for this user
          </div>
        )}
      </div>
    </div>
  );
}
